import { useState, useEffect } from 'react';
import { useOutletContext, Navigate, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { format } from 'date-fns';
import { QrCode, Download, Edit, Calendar, Users } from 'lucide-react';

interface LayoutContext {
  viewAsStoreId?: string;
  viewAsStoreName?: string;
}

interface QRCodeItem {
  id: string;
  code: string;
  url: string;
  status: string;
  qrImagePng: string;
  createdAt: string;
  run: {
    id: string;
    instanceNumber: number;
    status: string;
    startDate: string;
    endDate: string;
    campaign: {
      id: string;
      name: string;
      description: string | null;
    };
  };
  _count: {
    scans: number;
  };
}

export default function QRCodesPage() {
  const { viewAsStoreId, viewAsStoreName } = useOutletContext<LayoutContext>() || {};
  const navigate = useNavigate();
  const [qrCodes, setQRCodes] = useState<QRCodeItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Redirect if not viewing as a store
  if (!viewAsStoreId) {
    return <Navigate to="/admin/stores" replace />;
  }

  const goToDesigner = (qrCode: QRCodeItem) => {
    navigate(`/admin/qrcodes/${qrCode.id}?viewAs=${viewAsStoreId}`);
  };

  useEffect(() => {
    loadQRCodes();
  }, [viewAsStoreId]);

  const loadQRCodes = async () => {
    setLoading(true);
    setQRCodes([]);

    try {
      const response = await apiClient.get<{ success: boolean; data: QRCodeItem[] }>(
        `/admin/stores/${viewAsStoreId}/qrcodes`
      );
      setQRCodes(response.data.data);
    } catch (error) {
      console.error('Failed to load QR codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      ACTIVE: 'bg-green-100 text-green-800',
      PAUSED: 'bg-yellow-100 text-yellow-800',
      DRAFT: 'bg-gray-100 text-gray-800',
      COMPLETED: 'bg-blue-100 text-blue-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const downloadPDF = async (qrCode: QRCodeItem) => {
    try {
      const blob = await apiClient.getBlob(`/admin/stores/${viewAsStoreId}/qrcodes/${qrCode.id}/pdf`);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${viewAsStoreName}_${qrCode.run.campaign.name}_Run${qrCode.run.instanceNumber}_QR.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My QR Codes</h1>
      </div>

      {qrCodes.length === 0 ? (
        <div className="card text-center py-12">
          <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No QR codes available yet.</p>
          <p className="text-sm text-gray-400 mt-2">
            QR codes will appear here when you're enrolled in an active campaign.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {qrCodes.map((qr) => (
            <div key={qr.id} className="card hover:shadow-lg transition-shadow">
              {/* QR Code Preview */}
              <div className="flex justify-center mb-4">
                <div className="w-32 h-32 bg-white border-2 border-gray-100 rounded-lg overflow-hidden">
                  {qr.qrImagePng ? (
                    <img
                      src={qr.qrImagePng}
                      alt={`QR Code for ${qr.run.campaign.name}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                      <QrCode className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                </div>
              </div>

              {/* Campaign Info */}
              <div className="text-center mb-4">
                <h3 className="font-semibold text-gray-900">{qr.run.campaign.name}</h3>
                <p className="text-sm text-gray-500">Run #{qr.run.instanceNumber}</p>
              </div>

              {/* Status Badge */}
              <div className="flex justify-center mb-4">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(qr.run.status)}`}>
                  {qr.run.status}
                </span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(qr.run.startDate), 'MMM d')} - {format(new Date(qr.run.endDate), 'MMM d')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>{qr._count.scans} scans</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => goToDesigner(qr)}
                  className="flex-1 btn-secondary text-sm flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  View/Edit
                </button>
                <button
                  onClick={() => downloadPDF(qr)}
                  className="flex-1 btn-primary text-sm flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
